/**
 * Les deux ancres d'`expo-linear-gradient` pour un angle CSS, sur une boîte MESURÉE.
 *
 * `<LinearGradient>` ne prend pas d'angle : il prend `start` et `end`, deux points exprimés en
 * fractions de la boîte. Le vecteur réel du dégradé est donc `((endX − startX) · largeur,
 * (endY − startY) · hauteur)` — il dépend du rapport de la boîte, et deux ancres figées ne tiennent
 * leur angle que sur un carré. C'est ce qui donnait 99,6° au lieu de 120° sur la carte des
 * prochaines séances (1092 × 317).
 *
 * La conversion, dans le sens CSS (0° = vers le haut, les angles tournent dans le sens des
 * aiguilles ; y descend à l'écran) :
 *
 *  1. direction unitaire `d = (sin θ, −cos θ)` — pour 120° : `(0,866 ; 0,5)`, vers la droite et
 *     vers le bas ;
 *  2. longueur de la ligne de dégradé, au sens CSS : `L = |l · sin θ| + |h · cos θ|` — c'est ce
 *     qui fait que les deux arrêts extrêmes tombent sur les coins de la boîte ;
 *  3. les deux points, de part et d'autre du centre : `centre ∓ (L / 2) · d`, en pixels ;
 *  4. ramenés en fractions en divisant x par la largeur et y par la hauteur.
 *
 * Les ancres sortent de l'intervalle `[0, 1]` dès que la boîte n'est pas carrée : c'est normal et
 * voulu — elles désignent des points hors de la boîte, comme la ligne de dégradé de CSS.
 */

export type GradientAnchor = { x: number; y: number }
export type GradientAnchors = { start: GradientAnchor; end: GradientAnchor }

/**
 * @param angleDeg angle CSS en degrés (0° = vers le haut, sens horaire).
 * @param width largeur mesurée de la boîte, en px.
 * @param height hauteur mesurée de la boîte, en px.
 */
export function gradientAnchors(angleDeg: number, width: number, height: number): GradientAnchors {
    const radians = (angleDeg * Math.PI) / 180
    const dx = Math.sin(radians)
    const dy = -Math.cos(radians)

    // Une boîte pas encore mesurée vaut un carré : le rapport seul compte, et 1 × 1 rend les
    // ancres du carré plutôt que des NaN.
    const safeWidth = width > 0 ? width : 1
    const safeHeight = height > 0 ? height : 1

    const half = (Math.abs(safeWidth * dx) + Math.abs(safeHeight * dy)) / 2
    const offsetX = (half * dx) / safeWidth
    const offsetY = (half * dy) / safeHeight

    return {
        start: { x: 0.5 - offsetX, y: 0.5 - offsetY },
        end: { x: 0.5 + offsetX, y: 0.5 + offsetY },
    }
}
